import { useQuery } from '@tanstack/react-query';
import { api, type MarketContext } from '../lib/api';
import { fmt } from '../lib/format';

function formatOi(n: unknown): string {
  const v = Number(n);
  if (!Number.isFinite(v) || v === 0) return '—';
  if (v >= 1e9) return `${(v / 1e9).toFixed(2)}B`;
  if (v >= 1e6) return `${(v / 1e6).toFixed(2)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  return `${fmt.number(v, 0)}`;
}

function Metric({
  label, value, hint, warn,
}: { label: string; value: string; hint: string; warn?: boolean }) {
  return (
    <div className="g-card" style={{ padding: 18 }}>
      <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--gl-text-faint)' }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, marginTop: 8, color: warn ? 'var(--danger)' : 'var(--gl-text)' }}>{value}</div>
      <div style={{ fontSize: 12, color: 'var(--gl-text-muted)', marginTop: 8, lineHeight: 1.45 }}>{hint}</div>
    </div>
  );
}

/** Status chip untuk data source */
function SourceChip({ label, ok, err }: { label: string; ok: boolean; err?: string | null }) {
  return (
    <div className="g-card" style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
      <span style={{
        width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
        background: ok ? 'var(--gl-emerald)' : err ? 'var(--gl-rose)' : 'var(--gl-text-faint)',
        boxShadow: ok ? '0 0 6px rgba(52,211,153,0.5)' : 'none',
      }} />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--gl-text)' }}>{label}</div>
        {err && <div style={{ fontSize: 10, color: 'var(--gl-rose)', marginTop: 2 }}>{err}</div>}
        {ok && <div style={{ fontSize: 10, color: 'var(--gl-emerald)', marginTop: 2 }}>✓ Online</div>}
      </div>
    </div>
  );
}

export function MarketPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['context', 'BTCUSDT'],
    queryFn: () => api.get<MarketContext & { atr_14d?: number; raw?: any }>('/api/data/context?symbol=BTCUSDT'),
    refetchInterval: 45_000,
  });

  const spot = data?.spot_price;
  const maxPain = data?.max_pain;
  const distMp = spot && maxPain ? ((Number(spot) - Number(maxPain)) / Number(spot)) * 100 : null;
  const raw = data?.raw;

  // Data source status dari raw errors
  const sources = [
    { label: 'Binance Spot', ok: !raw?.spot_err, err: raw?.spot_err },
    { label: 'Deribit DVOL', ok: !raw?.dvol_err, err: raw?.dvol_err },
    { label: 'Binance Funding', ok: !raw?.funding_err, err: raw?.funding_err },
    { label: 'Binance OI', ok: !raw?.oi_err, err: raw?.oi_err },
    { label: 'Liquidation Boundaries', ok: data?.max_pain != null, err: null },
    { label: 'Kalender Makro', ok: !raw?.macro_err, err: raw?.macro_err, extra: raw?.macro_count != null ? `${raw.macro_count} event` : undefined },
    { label: 'Alternative.me F&G', ok: !raw?.fng_err, err: raw?.fng_err },
    { label: 'Max Pain (Deribit)', ok: data?.max_pain != null && Number(data.max_pain) > 0, err: null },
  ];

  return (
    <div className="gl-page">
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 20, fontWeight: 600, color: 'var(--gl-text)' }}>Market</div>
        <div style={{ fontSize: 12, color: 'var(--gl-text-muted)', marginTop: 4 }}>
          Data market BTC — detail untuk filter jarak, vol, funding, max pain. Status sumber data di bawah.
        </div>
      </div>

      {isLoading && <div className="gl-empty">Memuat market…</div>}
      {error && <div className="settings-alert settings-alert--error">Gagal: {(error as Error).message}</div>}

      {data && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14, marginBottom: 20 }}>
            <Metric
              label="BTC Spot"
              value={fmt.number(spot, 0)}
              hint="Harga spot Binance. Dipakai hitung jarak strike ke pasar."
            />
            <Metric
              label="DVOL (Deribit)"
              value={fmt.percent(data.dvol, 1)}
              hint="Implied vol forward. Tinggi → pasar bergolak, buffer jarak strike perlu lebih longgar."
              warn={Number(data.dvol) > 70}
            />
            <Metric
              label="Funding rate"
              value={fmt.percent(data.funding_rate != null ? Number(data.funding_rate) * 100 : null, 4)}
              hint="Funding futures (bukan APR DCD). Ekstrem satu arah = bias posisi spekulan."
            />
            <Metric
              label="Open interest"
              value={data.open_interest ? `${formatOi(data.open_interest)} kontrak` : '—'}
              hint="OI futures BTCUSDT (jumlah kontrak open). Bukan USD notional."
            />
            <Metric
              label="Max pain"
              value={fmt.number(maxPain, 0)}
              hint="Strike opsi di mana writer paling ‘sakit’. Soft score: strike dekat max pain lebih berisiko."
            />
            <Metric
              label="Spot vs max pain"
              value={distMp == null ? '—' : `${distMp > 0 ? '+' : ''}${distMp.toFixed(2)}%`}
              hint="Selisih % spot ke max pain. Positif = spot di atas max pain."
            />
          </div>

          {/* ===== Data Source Status ===== */}
          <div className="g-card" style={{ padding: 18, marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>📡 Status Sumber Data</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 8 }}>
              {sources.map((s) => (
                <SourceChip key={s.label} label={s.label} ok={s.ok} err={s.err} />
              ))}
            </div>
            <div style={{ fontSize: 10, color: 'var(--gl-text-faint)', marginTop: 10, lineHeight: 1.5 }}>
              {raw?.macro_count != null && `📅 ${raw.macro_count} high-impact event di kalender. `}
              Data otomatis tiap 45 detik. Sumber mati? Cek di VPS: <code>pm2 logs tc-data --lines 10</code>
            </div>
          </div>
        </>
      )}
    </div>
  );
}